import { ChangeEvent, forwardRef, useCallback, useEffect, useMemo, useState } from "react";
import { formatInTimeZone } from "date-fns-tz";
import React from "react";
import { ClientEndpointType, Device, Facility, LaneLine, MS2ClientEndpoint, Target } from "../../models";
import { ClientEndpoints, FacilityTargets } from "../../api";
import { getFHWAClassDisplayName } from "../../models/Shared.FHWAClass.gen";
import { getLaneDisplayName } from "../../models/Shared.Lane.gen";
import { getConfiguredLaneName } from "../../models/Shared.Lane.helpers";
import { getTurnTypeDisplayName } from "../../models/Shared.TurnType.gen";
import { getTargetTypeDisplayName } from "../../models/Shared.TargetType.gen";
import { buildPvrCsv, derivePvrHeaderIdentity, missingPvrHeaderFields, PvrHeaderIdentity, pvrCoordinates } from "./pvrExport";
import { LaneLabel } from "../LaneLabel";
import { CheckboxInput, Dialog, Icons, Input, SortableTable, SortableTableHeaderType, InfiniteScrollOptions } from "@app/dashboard/src/components/shared";
import { FilterOption } from "@app/dashboard/src/components/shared/Filter";
import { AllFHWAClasses, AllLanes, AllTargetTypes, AllTurns } from "@app/dashboard/src/components/reports/reportHelpers";
import { debounce, toastWarning } from "@app/dashboard/src/helpers";
import { LiveViewMode } from "./LiveViewProvider";

export interface RecentTargetsTableProps {
    mode: LiveViewMode;
    devices: Device[];
    laneLines?: LaneLine[];
    facility?: Facility;
    facilityId?: string;
    timezoneId?: string;
    periodStart?: Date;
    periodEnd?: Date;
    localTargets?: Target[];
    onSelectedChange: (target: Target | undefined, selected: boolean) => void;
    onRowClick: (target: Target) => void;
    externalSelectedIds?: Set<string>;
    // Facility mode only: notifies the parent whether the current sort is the default
    // (newest-first). The parent uses this to pause/resume live updates.
    onSortStateChange?: (isDefaultSort: boolean) => void;
    /**
     * Drops the Lane and Turn columns. Those describe how a vehicle moved through an intersection,
     * which says nothing at a site that counts an entry and an exit ramp — see the Parking View's
     * in/out mode.
     */
    hideLaneAndTurn?: boolean;
    /**
     * Adds an In/Out column from each target's own NetFacilityCountChange. At an in/out site the
     * direction *is* the point: without it the table says what came through but not which way, and
     * which way is the entire count.
     */
    showFacilityDirection?: boolean;
}

type TargetQuery = {
    limit: number;
    sortKey?: string;
    sortDirection?: "asc" | "desc";
    filters?: FilterOption<Target>[];
    cursor?: string;
};

type PvrPromptState = {
    targets: Target[];
    identity: PvrHeaderIdentity;
    missing: Array<{ key: keyof PvrHeaderIdentity; label: string }>;
};

export interface RecentTargetsMethods {
    downloadCSV: () => void;
    downloadPvrCSV: () => void;
    addNewTargets: (targets: Target[], shouldSelect: boolean) => void;
    // Resets the sort back to the default (newest-first), which lets the parent resume live updates.
    resetToLiveDefault: () => void;
}

// Stable tiebreak by id, matching the server's ThenBy(e => e.Id) direction.
const idTiebreak = (a: Target, b: Target, dir: number) => dir * String(a.id ?? "").localeCompare(String(b.id ?? ""));

// Display comparator for the loaded set. Mirrors the server ordering
// (TargetProvider.ApplySorting): NULLs/undefined last in both directions, then by
// value, then by id. Applied to both device-mode (fully client-side) and facility-mode
// (so live-prepended targets render in their sorted position rather than at the top).
const compareTargets = (a: Target, b: Target, sortKey: string, sortDirection: "asc" | "desc") => {
    const dir = sortDirection === "asc" ? 1 : -1;
    const aVal = a[sortKey as keyof Target];
    const bVal = b[sortKey as keyof Target];

    const aNull = aVal === undefined || aVal === null;
    const bNull = bVal === undefined || bVal === null;
    if (aNull && bNull) return idTiebreak(a, b, dir);
    if (aNull) return 1; // nulls last regardless of direction
    if (bNull) return -1;

    let cmp = 0;
    if (aVal instanceof Date && bVal instanceof Date) {
        cmp = aVal.getTime() - bVal.getTime();
    } else if (typeof aVal === "number" && typeof bVal === "number") {
        cmp = aVal - bVal;
    } else {
        cmp = String(aVal).localeCompare(String(bVal));
    }

    return cmp !== 0 ? dir * cmp : idTiebreak(a, b, dir);
};

const targetMatchesFilters = (target: Target, filters: FilterOption<Target>[] | undefined) => {
    if (!filters || filters.length === 0) return true;
    return filters.every((filter) => {
        if (filter.value === undefined || filter.value === "") return true;
        const filterValue = filter.value;
        switch (filter.id) {
            case "type":
                return target.type === parseInt(filterValue);
            case "fhwaClass":
                return target.fhwaClass === parseInt(filterValue);
            case "lanes":
                return target.lanes === parseInt(filterValue);
            case "turn":
                return target.turn === parseInt(filterValue);
            case "deviceId":
                return target.deviceId === filterValue;
            default:
                return true;
        }
    });
};

const triggerDownload = (csvContent: string, fileName: string) => {
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
};

export const RecentTargetsTable = forwardRef<RecentTargetsMethods, RecentTargetsTableProps>(({ mode, facility, facilityId, devices, laneLines, timezoneId, onSelectedChange, onRowClick, periodEnd, periodStart, localTargets, externalSelectedIds, onSortStateChange, hideLaneAndTurn, showFacilityDirection }, ref) => {
    const isDeviceMode = mode === "device";
    const getDeviceNameForTarget = (target?: Target) => devices?.find((d) => d.id === target?.deviceId)?.name ?? "";

    const [apiTargets, setApiTargets] = useState<Target[]>([]);
    const [selectedTargets, setSelectedTargets] = useState<Target[]>([]);
    const selectedTargetsRef = React.useRef(selectedTargets);
    selectedTargetsRef.current = selectedTargets;
    const onSelectedChangeRef = React.useRef(onSelectedChange);
    onSelectedChangeRef.current = onSelectedChange;
    const [isLoading, setIsLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [nextCursor, setNextCursor] = useState<string | undefined>();

    const [localSortKey, setLocalSortKey] = useState<string>("lastUpdate");
    const [localSortDirection, setLocalSortDirection] = useState<"asc" | "desc">("desc");
    const [localFilters, setLocalFilters] = useState<FilterOption<Target>[]>([]);

    const [pvrIdentityOverrides, setPvrIdentityOverrides] = useState<Partial<PvrHeaderIdentity>>({});
    const [pvrPrompt, setPvrPrompt] = useState<PvrPromptState | undefined>();
    const [pvrPromptValues, setPvrPromptValues] = useState<Partial<PvrHeaderIdentity>>({});

    const [query, setQuery] = useState<TargetQuery>({
        limit: 100,
        sortKey: "lastUpdate",
        sortDirection: "desc",
    });

    // The default sort (newest-first) is the only one compatible with live streaming —
    // any other sort makes the parent pause live updates. Report changes upward.
    const isDefaultSort = (query.sortKey ?? "lastUpdate") === "lastUpdate" && (query.sortDirection ?? "desc") === "desc";
    useEffect(() => {
        if (!isDeviceMode) {
            onSortStateChange?.(isDefaultSort);
        }
    }, [isDeviceMode, isDefaultSort, onSortStateChange]);

    const zone = timezoneId ?? Intl.DateTimeFormat(undefined, { timeZoneName: "short" }).resolvedOptions().timeZone;
    const timeZone = useMemo(() => formatInTimeZone(new Date(), zone, "z"), [zone]);

    const targets = useMemo(() => {
        const sortKey = isDeviceMode ? localSortKey : (query.sortKey ?? "lastUpdate");
        const sortDirection = isDeviceMode ? localSortDirection : (query.sortDirection ?? "desc");

        // Device mode is fully client-side (filter + sort the local set). Facility mode
        // is server-paginated, but we still sort the loaded set client-side so that
        // live-prepended targets (addNewTargets) settle into their correct sorted
        // position instead of always sitting at the top.
        const source = isDeviceMode
            ? (localTargets || []).filter((target) => targetMatchesFilters(target, localFilters))
            : [...apiTargets];

        return source.sort((a, b) => compareTargets(a, b, sortKey, sortDirection));
    }, [isDeviceMode, localTargets, apiTargets, localSortKey, localSortDirection, localFilters, query.sortKey, query.sortDirection]);

    const convertFiltersToParams = (filters?: FilterOption<Target>[]) => {
        const params: {
            targetType?: number;
            fhwaClass?: number;
            turn?: number;
            lane?: number;
            deviceId?: string;
        } = {};

        if (!filters) return params;

        filters.forEach((filter) => {
            if (filter.value !== undefined && filter.value !== "") {
                switch (filter.id) {
                    case "type":
                        params.targetType = parseInt(filter.value);
                        break;
                    case "deviceId":
                        params.deviceId = filter.value;
                        break;
                    case "turn":
                        params.turn = filter.value;
                        break;
                    case "lanes":
                        params.lane = filter.value;
                        break;
                    case "fhwaClass":
                        params.fhwaClass = parseInt(filter.value);
                        break;
                }
            }
        });

        return params;
    };

    const fetchTargets = useCallback(
        async (reset = false, cursor?: string) => {
            if (isDeviceMode || !facilityId) return;

            setIsLoading(true);
            try {
                const filterParams = convertFiltersToParams(query.filters);
                const response = await FacilityTargets.getByFacilityPaginated(facilityId, 50, query.sortKey, query.sortDirection, filterParams.deviceId, filterParams.targetType, filterParams.fhwaClass, filterParams.lane, filterParams.turn, periodStart, periodEnd, cursor);

                if (reset) {
                    setApiTargets(response?.data || []);
                } else {
                    setApiTargets((prev) => {
                        const existingIds = new Set(prev.map((t) => t.id));
                        const newTargets = (response?.data || []).filter((t) => !existingIds.has(t.id));
                        const mergedTargets = [...prev, ...newTargets];
                        const mergedIds = new Set(mergedTargets.map((t) => t.id));

                        const currentSelected = selectedTargetsRef.current;
                        const removed = currentSelected.filter((t) => !mergedIds.has(t.id));
                        removed.forEach((removedTarget) => {
                            onSelectedChangeRef.current(removedTarget, false);
                        });
                        setSelectedTargets(currentSelected.filter((t) => mergedIds.has(t.id)));
                        return mergedTargets;
                    });
                }

                setHasMore(response?.hasMore || false);
                setNextCursor(response?.nextCursor);
            } catch (error) {
                console.error("Failed to fetch targets:", error);
                toastWarning("Failed to load targets. Please try again later.");
            } finally {
                setIsLoading(false);
            }
        },
        [isDeviceMode, facilityId, query, periodEnd, periodStart]
    );

    const updateSelection = (target: Target, event: ChangeEvent<HTMLInputElement>) => {
        setSelectedTargets((prev) => {
            if (!event.target.checked) {
                return prev.filter((x) => x.id !== target.id);
            }
            return [...prev, target];
        });
        onSelectedChange(target, event.target.checked);
    };

    const debouncedFetchTargets = useMemo(() => debounce((reset: boolean) => fetchTargets(reset), 300), [fetchTargets]);

    useEffect(() => {
        if (!isDeviceMode && facilityId) {
            setApiTargets([]);
            setNextCursor(undefined);
            setHasMore(true);
            debouncedFetchTargets(true);
        }
    }, [isDeviceMode, facilityId, debouncedFetchTargets]);

    const handleLoadMore = useCallback(async () => {
        if (hasMore && !isLoading && nextCursor) {
            await fetchTargets(false, nextCursor);
        }
    }, [hasMore, isLoading, nextCursor, fetchTargets]);

    const infiniteScrollOptions: InfiniteScrollOptions = {
        enabled: true, // always enable for virtualized scrolling
        onLoadMore: isDeviceMode ? async () => {} : handleLoadMore,
        hasMore: isDeviceMode ? false : hasMore,
        isLoading: isDeviceMode ? false : isLoading,
        overscan: 5,
        followOutput: false,
    };

    const deviceFilterOptions = devices.map((dev) => ({
        value: dev.id ?? "",
        label: dev.name,
    }));
    const fhwaFilterOptions = AllFHWAClasses.map((x) => ({ label: getFHWAClassDisplayName(x) ?? "", value: String(x) }));
    const ttFilterOptions = AllTargetTypes.map((x) => ({ label: getTargetTypeDisplayName(x) ?? "", value: String(x) }));
    const laneOptions = AllLanes.map((x) => ({ label: getLaneDisplayName(x) ?? "", value: String(x) }));
    const turnOptions = AllTurns.map((x) => ({ label: getTurnTypeDisplayName(x) ?? "", value: String(x) }));

    const columns = useMemo(() => {
        const cols: SortableTableHeaderType<Target>[] = [
            {
                header: "Show Tracks",
                width: 75,
                renderFunc: (t?: Target) =>
                    t ? (
                        <div onClick={(e) => e.stopPropagation()} className="cursor-default">
                            <CheckboxInput onChange={(e) => updateSelection(t, e)} checked={externalSelectedIds ? externalSelectedIds.has(t.id!) : !!selectedTargets.find((x) => x.id === t.id)} />
                        </div>
                    ) : null,
            },
            {
                dataKey: "lastUpdate",
                header: `Date (${timeZone})`,
                width: 135,
                renderFunc: (target?: Target) => (target?.lastUpdate ? formatInTimeZone(target.lastUpdate, zone, "MMM d, HH:mm") : ""),
            },
        ];

        // only show device column in facility mode (multiple devices)
        if (!isDeviceMode) {
            cols.push({
                dataKey: "deviceId",
                header: "Detection Device",
                width: 175,
                renderFunc: (target?: Target) => getDeviceNameForTarget(target),
                filterable: true,
                filterType: "select",
                filterOptions: deviceFilterOptions,
            } as SortableTableHeaderType<Target>);
        }

        cols.push(
            {
                dataKey: "type",
                header: "Target Type",
                width: 100,
                renderFunc: (target?: Target) => (target?.type !== undefined ? getTargetTypeDisplayName(target.type) : ""),
                filterable: true,
                filterType: "select",
                filterOptions: ttFilterOptions,
            } as SortableTableHeaderType<Target>,
            {
                dataKey: "fhwaClass",
                header: "FHWA Class",
                width: 100,
                renderFunc: (target?: Target) => (target?.fhwaClass ? getFHWAClassDisplayName(target.fhwaClass) : ""),
                filterable: true,
                filterType: "select",
                filterOptions: fhwaFilterOptions,
            } as SortableTableHeaderType<Target>,
            ...(hideLaneAndTurn ? [] : [
            {
                dataKey: "lanes",
                header: "Lane",
                width: 100,
                renderFunc: (target?: Target) =>
                    target?.lanes ? <LaneLabel lanes={target.lanes} laneLines={laneLines?.filter((ll) => !ll.deviceId || ll.deviceId === target.deviceId)} emptyValue="" /> : "",
                filterable: true,
                filterType: "select",
                filterOptions: laneOptions,
            } as SortableTableHeaderType<Target>,
            {
                dataKey: "turn",
                header: "Turn",
                width: 100,
                renderFunc: (target?: Target) => (target?.turn ? getTurnTypeDisplayName(target.turn) : ""),
                filterable: true,
                filterType: "select",
                filterOptions: turnOptions,
            } as SortableTableHeaderType<Target>,
            ]),
            {
                dataKey: "maximumWidth",
                header: "Size (m)",
                width: 100,
                renderFunc: (target?: Target) => `(${target?.maximumWidth?.toFixed(1) ?? "?"}, ${target?.maximumHeight?.toFixed(1) ?? "?"})`,
            },
            {
                dataKey: "averageVelocity",
                header: "Avg Velocity",
                width: 100,
                renderFunc: (target?: Target) => `${((target?.averageVelocity ?? 0) * 2.237).toFixed(1)} mph`,
            },
            {
                // Already the in/out indicator; at a site that counts by entry-minus-exit it stops
                // being a decoration and becomes the column that matters, so it gets a header and
                // room for a word rather than staying a bare 25px icon.
                header: showFacilityDirection ? "In/Out" : "",
                renderFunc: (target?: Target) => {
                    const net = target?.netFacilityCountChange ?? 0;
                    const icon = net > 0 ? <Icons.Entry color="green" className="w-6 h-6" />
                        : net < 0 ? <Icons.Exit color="red" className="w-6 h-6" /> : null;
                    if (!showFacilityDirection) return icon;
                    return (
                        <span className="flex items-center gap-1">
                            {icon}
                            {net > 0 ? "In" : net < 0 ? "Out" : <span className="text-muted-body-text">—</span>}
                        </span>
                    );
                },
                width: showFacilityDirection ? 90 : 25,
            }
        );

        return cols;
    }, [isDeviceMode, selectedTargets, externalSelectedIds, timeZone, zone, deviceFilterOptions, ttFilterOptions, fhwaFilterOptions, laneOptions, turnOptions, hideLaneAndTurn, showFacilityDirection]);

    const resolveExportData = useCallback(async (): Promise<Target[]> => {
        if (isDeviceMode) return targets;
        if (!facilityId) return [];
        const filterParams = convertFiltersToParams(query.filters);
        const data = await FacilityTargets.getByFacilityPaginated(facilityId, 50000, query.sortKey, query.sortDirection, filterParams.deviceId, filterParams.targetType, filterParams.fhwaClass, filterParams.lane, filterParams.turn, periodStart, periodEnd, undefined);
        return data?.data || [];
    }, [isDeviceMode, targets, facilityId, query, periodStart, periodEnd]);

    const downloadCSV = useCallback(async () => {
        const dataToExport = await resolveExportData();
        if (!dataToExport.length) return;

        try {
            const headers = ["ID", "Device", "Target Type", "FHWA Class", "Lane", "Turn", "Last Update (UTC)", `Last Update (${timeZone})`, "Max Velocity (mph)", "Avg Velocity (mph)", "Width (m)", "Height (m)", "Point Count", "Entry/Exit", "Lane (Configured)"];

            const rows = dataToExport.map((target) => [
                target.id ?? "",
                getDeviceNameForTarget(target),
                getTargetTypeDisplayName(target.type),
                target.fhwaClass ? getFHWAClassDisplayName(target.fhwaClass) : "",
                target.lanes ? getLaneDisplayName(target.lanes as number) : "",
                target.turn ? getTurnTypeDisplayName(target.turn) : "",
                target.lastUpdate?.toISOString().replace("Z", "") ?? "",
                target.lastUpdate ? formatInTimeZone(target.lastUpdate, zone, "yyyy-MM-dd'T'HH:mm:ss.SSS") : "",
                target.maximumVelocity ? (target.maximumVelocity * 2.237).toFixed(2) : "",
                target.averageVelocity ? (target.averageVelocity * 2.237).toFixed(2) : "",
                target.maximumWidth?.toFixed(2) ?? "",
                target.maximumHeight?.toFixed(2) ?? "",
                target.maxPointCount?.toString() ?? "",
                target.netFacilityCountChange?.toString() ?? "0",
                getConfiguredLaneName(target.lanes, laneLines?.filter((ll) => !ll.deviceId || ll.deviceId === target.deviceId))?.primary ?? "",
            ]);

            const csvContent = [headers.map((h) => `"${h}"`).join(","), ...rows.map((row) => row.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(","))].join("\n");
            triggerDownload(csvContent, `Targets_${new Date().toISOString()}.csv`);
        } catch (error) {
            console.error("Download failed:", error);
        }
    }, [resolveExportData, getDeviceNameForTarget, laneLines, timeZone, zone]);

    const resolvePvrLocationId = useCallback(async (): Promise<string | undefined> => {
        if (isDeviceMode || !facilityId) return undefined;
        try {
            const endpoints = await ClientEndpoints.getAll(undefined, facilityId);
            const ms2 = endpoints?.find((e) => e.endpointType === ClientEndpointType.MS2) as MS2ClientEndpoint | undefined;
            return ms2?.mS2LocationId || undefined;
        } catch {
            return undefined;
        }
    }, [isDeviceMode, facilityId]);

    const emitPvrCsv = useCallback(
        (dataToExport: Target[], identity: PvrHeaderIdentity) => {
            const fileDate = formatInTimeZone(periodStart ?? new Date(), zone, "yyyyMMdd");
            const station = identity.stationNumber.replace(/[^A-Za-z0-9_-]/g, "");
            const fileName = `PerVehicle_${station ? `${station}_` : ""}${fileDate}.csv`;
            const { csv, exportedCount, skippedCount } = buildPvrCsv(dataToExport, { zone, reportStart: periodStart, fileName, identity, ...pvrCoordinates(facility) });
            triggerDownload(csv, fileName);
            if (skippedCount > 0) {
                toastWarning(`${exportedCount} records exported. ${skippedCount} incomplete records were left out.`);
            }
        },
        [periodStart, zone, facility]
    );

    const downloadPvrCSV = useCallback(async () => {
        const dataToExport = await resolveExportData();
        if (!dataToExport.length) return;

        try {
            const locationId = await resolvePvrLocationId();
            const identity = { ...derivePvrHeaderIdentity(facility, devices, locationId), ...pvrIdentityOverrides };
            const missing = missingPvrHeaderFields(identity);
            if (missing.length) {
                setPvrPromptValues({});
                setPvrPrompt({ targets: dataToExport, identity, missing });
                return;
            }
            emitPvrCsv(dataToExport, identity);
        } catch (error) {
            console.error("Download failed:", error);
        }
    }, [resolveExportData, resolvePvrLocationId, emitPvrCsv, facility, devices, pvrIdentityOverrides]);

    const pvrPromptIncomplete = (pvrPrompt?.missing ?? []).some(({ key }) => !pvrPromptValues[key]?.trim());

    const handlePvrPromptOk = () => {
        if (!pvrPrompt || pvrPromptIncomplete) return;
        const entered = Object.fromEntries(pvrPrompt.missing.map(({ key }) => [key, pvrPromptValues[key]!.trim()])) as Partial<PvrHeaderIdentity>;
        setPvrIdentityOverrides((prev) => ({ ...prev, ...entered }));
        setPvrPrompt(undefined);
        emitPvrCsv(pvrPrompt.targets, { ...pvrPrompt.identity, ...entered });
    };

    const addNewTargets = (newTargets: Target[], shouldSelect: boolean) => {
        if (isDeviceMode) {
            return;
        }
        const matchingTargets = newTargets.filter((t) => targetMatchesFilters(t, query.filters));
        if (matchingTargets.length === 0) return;
        setApiTargets((prev) => [...matchingTargets, ...prev]);
        if (shouldSelect) {
            setSelectedTargets((prev) => [...matchingTargets, ...prev]);
        }
    };

    const resetToLiveDefault = () => {
        if (isDeviceMode) return;
        setQuery((prev) => ({ ...prev, sortKey: "lastUpdate", sortDirection: "desc", cursor: undefined }));
        setNextCursor(undefined);
        setHasMore(true);
        setApiTargets([]);
        setIsLoading(true);
    };

    React.useImperativeHandle(ref, () => ({
        downloadCSV,
        downloadPvrCSV,
        addNewTargets,
        resetToLiveDefault,
    }));

    const handleSort = (sortKey: string, direction: "asc" | "desc") => {
        if (isDeviceMode) {
            setLocalSortKey(sortKey);
            setLocalSortDirection(direction);
        } else {
            setQuery((prev) => ({
                ...prev,
                sortKey,
                sortDirection: direction,
                cursor: undefined,
            }));
            setNextCursor(undefined);
            setHasMore(true);
            setApiTargets([]);
            setIsLoading(true);
        }
    };

    const handleFilter = (filters: FilterOption<Target>[]) => {
        if (isDeviceMode) {
            setLocalFilters(filters);
            return;
        }

        setQuery((prev) => ({
            ...prev,
            filters,
            cursor: undefined,
        }));
        setNextCursor(undefined);
        setHasMore(true);
        setApiTargets([]);
        setIsLoading(true);
    };

    return (
        <>
            <SortableTable onRowClick={onRowClick} data={targets} onSort={handleSort} onFilter={handleFilter} sortKey={isDeviceMode ? localSortKey : query.sortKey} sortDirection={isDeviceMode ? localSortDirection : query.sortDirection} isLoading={!isDeviceMode && isLoading} emptyValue="No recent targets" columns={columns} filtering={true} mode="infiniteScroll" infiniteScroll={infiniteScrollOptions} />
            <Dialog show={!!pvrPrompt} title="Per-Vehicle Record Export" okButtonText="Export" disableOkButton={pvrPromptIncomplete} onOkClick={handlePvrPromptOk} onCancelClick={() => setPvrPrompt(undefined)}>
                <div className="flex flex-col gap-4 w-96">
                    <p className="text-muted-body-text">This export needs the values below and they are not set on the facility. They are kept until you leave or reload this page.</p>
                    {(pvrPrompt?.missing ?? []).map(({ key, label }) => (
                        <Input key={key} label={label} value={pvrPromptValues[key] ?? ""} onChange={(e: ChangeEvent<HTMLInputElement>) => setPvrPromptValues((prev) => ({ ...prev, [key]: e.target.value }))} />
                    ))}
                </div>
            </Dialog>
        </>
    );
});

RecentTargetsTable.displayName = "RecentTargetsTable";
